import React from 'react';
import SEO from '../components/SEO';

const BOOKING_FEES = [
  { service: 'Domestic One-Way', fee: '$9.99', note: 'Per booking (all passengers)' },
  { service: 'Domestic Round-Trip', fee: '$14.99', note: 'Per booking (all passengers)' },
  { service: 'International One-Way', fee: '$19.99', note: 'Per booking (all passengers)' },
  { service: 'International Round-Trip', fee: '$29.99', note: 'Per booking (all passengers)' },
  { service: 'Multi-City', fee: '$34.99', note: 'Per booking (all passengers)' },
];

const CHANGE_FEES = [
  { service: 'Date / Time Change', fee: '$25 + airline fee', note: 'Plus any fare difference' },
  { service: 'Route Change', fee: '$35 + airline fee', note: 'Plus any fare difference' },
  { service: 'Passenger Name Correction', fee: '$20', note: 'Minor corrections only (≤3 characters)' },
  { service: 'Seat Selection Assistance', fee: '$10', note: 'Per passenger, per leg' },
  { service: 'Baggage Add-On Request', fee: '$10', note: 'Per passenger (airline rates apply separately)' },
];

const CANCEL_FEES = [
  { service: 'Refundable Fare Cancellation', fee: '$25', note: 'Deducted from refund amount' },
  { service: 'Non-Refundable Fare Cancellation', fee: '$15', note: 'Processing fee (no fare refund)' },
  { service: 'Upgrade to Refundable Fare', fee: '$49.99', note: 'Before departure, subject to availability' },
];

const FREE_SERVICES = [
  'Flight status updates via email',
  'PNR / booking confirmation emails',
  'Fare alerts for saved routes',
  '24/7 chat support (basic inquiries)',
  'My Bookings dashboard access',
  'Itinerary PDF download',
];

function FeeTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wide">
          <tr>
            <th className="text-left px-5 py-3">Service</th>
            <th className="text-left px-5 py-3">Our Fee</th>
            <th className="text-left px-5 py-3">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row) => (
            <tr key={row.service} className="hover:bg-gray-50 transition-colors">
              <td className="px-5 py-4 font-medium text-gray-800">{row.service}</td>
              <td className="px-5 py-4">
                <span className="inline-block bg-orange-100 text-orange-700 font-bold px-2.5 py-0.5 rounded-lg text-xs">
                  {row.fee}
                </span>
              </td>
              <td className="px-5 py-4 text-gray-500">{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ServiceFeesPage() {
  return (
    <div>
      <SEO
        title="Service Fees"
        canonical="/service-fees"
        description="1ClickBooking transparent service fee schedule — booking fees, change fees, cancellation fees, and what's always free when you book with us."
        keywords="service fees, booking fees, flight change fee, cancellation fee, 1clickbooking fees"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 sm:py-20 px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
          <span>💳</span> Effective: June 1, 2025
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Service Fees</h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          Transparent pricing — no hidden charges. Our fees are listed upfront so you always know what you're paying.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16 space-y-12">

        {/* Why we charge */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: '🔍', title: 'Search & Compare', desc: 'We search 500+ airlines and fares in real time to find you the best price.' },
            { icon: '📞', title: '24/7 Support', desc: 'Our team is available around the clock for booking help and emergencies.' },
            { icon: '🛡️', title: 'Booking Protection', desc: 'We monitor your flight and proactively notify you of changes or issues.' },
          ].map(item => (
            <div key={item.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
              <div className="text-3xl mb-2">{item.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Booking fees */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Booking Fees</h2>
          <p className="text-gray-500 text-sm mb-5">Charged once per booking at the time of purchase. Covers all passengers in the same booking.</p>
          <FeeTable rows={BOOKING_FEES} />
        </div>

        {/* Change fees */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Change & Modification Fees</h2>
          <p className="text-gray-500 text-sm mb-5">In addition to any airline-imposed change fees. Airline fees vary by carrier and fare rules.</p>
          <FeeTable rows={CHANGE_FEES} />
        </div>

        {/* Cancellation fees */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Cancellation Fees</h2>
          <p className="text-gray-500 text-sm mb-5">Our processing fees for handling cancellations. Airline penalties are separate and governed by fare rules.</p>
          <FeeTable rows={CANCEL_FEES} />
        </div>

        {/* Free services */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Always Free — No Charge</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FREE_SERVICES.map(service => (
              <div key={service} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm">{service}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Important notes */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Important Notes</h2>
          {[
            { icon: 'ℹ️', text: 'All fees are in USD and are non-refundable once a service has been rendered.' },
            { icon: '⚡', text: 'Fees are clearly shown at checkout before you confirm and pay — no surprises.' },
            { icon: '🏷️', text: 'Airline-imposed fees (change penalties, baggage) are separate from our service fees.' },
            { icon: '📧', text: 'A fee receipt is included in your booking confirmation email for all charges.' },
          ].map(item => (
            <div key={item.text} className="flex items-start gap-3">
              <span className="text-lg mt-0.5">{item.icon}</span>
              <p className="text-gray-600 text-sm">{item.text}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-gray-700 font-semibold mb-1">Have a question about fees?</p>
          <p className="text-gray-500 text-sm mb-4">Our support team can walk you through what applies to your specific booking.</p>
          <a
            href="mailto:support@1clickbooking.us"
            className="inline-block bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold px-6 py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity"
          >
            Contact Support →
          </a>
        </div>
      </div>
    </div>
  );
}
